import { loadStripe } from "@stripe/stripe-js";
import { api } from "./http-client";
import { API_PATHS } from "../config/api.config";
import {
  Plan,
  PendingPlanChange,
  SubscriptionState,
} from "../interfaces/billing.interface";

/**
 * Runs the bank's 3D Secure check when the server says a charge needs one.
 * Sync runs either way: on success it writes the paid change, on failure it
 * voids the unpaid invoice. A failed or dismissed check rejects with the API's
 * `{response.data.error.tag}` shape, so the pages' tag handling covers it.
 */
const confirmCharge = async (clientSecret: string | null | undefined) => {
  if (!clientSecret) return;
  const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ?? "");
  const result = stripe ? await stripe.handleNextAction({ clientSecret }) : null;
  await api.request({ url: API_PATHS.BILLING.SYNC, method: "POST" });
  if (!result || result.error) {
    throw { response: { data: { error: { tag: "authentication_failed" } } } };
  }
};

export const billingService = {
  getPlans: async (lang: string): Promise<Plan[]> =>
    (
      await api.request<{ plans: Plan[] }>({
        url: API_PATHS.BILLING.PLANS,
        method: "GET",
        params: { lang },
      })
    ).data.plans,

  getSubscription: async (): Promise<SubscriptionState> =>
    (
      await api.request<SubscriptionState>({
        url: API_PATHS.BILLING.SUBSCRIPTION,
        method: "GET",
      })
    ).data,

  /**
   * The body carries planCode and addonQuantity and nothing else. Sending a
   * price, an amount, or a userId is rejected by the server's Joi schema
   * (allowUnknown is off) — and would be a client setting its own price if it
   * were not. The add-on total is priced server-side from the plan row.
   */
  createCheckoutSession: async (
    planCode: string,
    addonQuantity: number,
  ): Promise<{ url: string }> =>
    (
      await api.request<{ url: string }>({
        url: API_PATHS.BILLING.CHECKOUT_SESSION,
        method: "POST",
        data: { planCode, addonQuantity },
      })
    ).data,

  createPortalSession: async (): Promise<{ url: string }> =>
    (
      await api.request<{ url: string }>({
        url: API_PATHS.BILLING.PORTAL_SESSION,
        method: "POST",
      })
    ).data,

  /**
   * The plan a scheduled downgrade will land on, read live from Stripe.
   * Resolves null when nothing is pending. Account-only — never on Mobile's
   * hot path (getSubscription stays DB-only).
   */
  getPendingChange: async (): Promise<PendingPlanChange | null> =>
    (
      await api.request<PendingPlanChange | null>({
        url: API_PATHS.BILLING.PENDING_CHANGE,
        method: "GET",
      })
    ).data,

  /** Undoes a pending cancellation (cancel_at_period_end -> false). */
  resume: async (): Promise<{
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: string | null;
  }> =>
    (
      await api.request<{
        cancelAtPeriodEnd: boolean;
        currentPeriodEnd: string | null;
      }>({
        url: API_PATHS.BILLING.RESUME,
        method: "POST",
      })
    ).data,

  /**
   * Switches to a more expensive plan immediately, charging the prorated
   * difference now. planCode is the only identifier the body carries — same
   * rule as checkout (the server's Joi schema rejects anything else).
   */
  upgrade: async (planCode: string): Promise<{ planCode: string }> => {
    const { data } = await api.request<{ planCode: string; clientSecret?: string | null }>({
      url: API_PATHS.BILLING.UPGRADE,
      method: "POST",
      data: { planCode },
    });
    await confirmCharge(data.clientSecret);
    return { planCode: data.planCode };
  },

  /** Schedules a switch to a cheaper plan at period end. */
  downgrade: async (planCode: string): Promise<{ planCode: string }> =>
    (
      await api.request<{ planCode: string }>({
        url: API_PATHS.BILLING.DOWNGRADE,
        method: "POST",
        data: { planCode },
      })
    ).data,

  /** The prorated charge, now, for raising the add-on count. Writes nothing. */
  previewAddons: async (
    quantity: number,
  ): Promise<{ amountDue: number; currency: string }> =>
    (
      await api.request<{ amountDue: number; currency: string }>({
        url: API_PATHS.BILLING.ADDONS_PREVIEW,
        method: "POST",
        data: { quantity },
      })
    ).data,

  /** Increase: charged now (3D Secure if the bank asks). Decrease: scheduled for renewal. */
  updateAddons: async (quantity: number): Promise<void> => {
    const { data } = await api.request<{ clientSecret?: string | null }>({
      url: API_PATHS.BILLING.ADDONS,
      method: "POST",
      data: { quantity },
    });
    await confirmCharge(data?.clientSecret);
  },
};
